import type { JSX } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { COLUMN_ROLES, type ColumnMapping, type ColumnRole, type CreateJobResponse } from '@profiler/shared'
import { checkMapping } from './state'

const ROLE_LABELS: Record<ColumnRole, string> = {
  company_name: 'Company name',
  website: 'Website',
  linkedin_url: 'Company LinkedIn URL',
  country: 'Country',
  target_roles: 'Target roles',
  passthrough: 'Passthrough only',
}

const ROLE_HINTS: Record<ColumnRole, string> = {
  company_name: 'Kept verbatim in the export so it still joins to the CRM',
  website: 'Resolved and classified before any credit is spent',
  linkedin_url: 'The strongest anchor there is: it skips company resolution entirely',
  country: 'Used by the country gate when the match is weaker than a URL match',
  target_roles: 'Per-row role overrides, merged with the persona preset',
  passthrough: 'Carried through to the export untouched',
}

function sampleValues(rows: Array<Record<string, string>>, header: string): string {
  const values = rows.map((row) => row[header] ?? '').filter((v) => v.trim().length > 0)
  return values.slice(0, 3).join(' · ')
}

export function StepMapping({
  created,
  mapping,
  onChange,
}: Readonly<{
  created: CreateJobResponse
  mapping: ColumnMapping
  onChange: (mapping: ColumnMapping) => void
}>): JSX.Element {
  const issues = checkMapping(mapping)

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Each detected column is pre-assigned from the header text. Correct anything that is wrong: the roles
        decide which zero-credit rungs can run before the profiler starts paying for lookups.
      </Typography>

      {issues.map((issue) => (
        <Alert key={issue.message} severity={issue.severity}>
          {issue.message}
        </Alert>
      ))}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: '28%' }}>Column in the file</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '26%' }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Sample values</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {created.headers.map((header) => {
              const role: ColumnRole = mapping[header] ?? 'passthrough'
              return (
                <TableRow key={header} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{header}</TableCell>
                  <TableCell>
                    <Select
                      fullWidth
                      value={role}
                      // The column name sits in the cell alongside, which is not
                      // announced with the combobox, so a twenty-column sheet
                      // otherwise reads as twenty identical unnamed selects.
                      inputProps={{ 'aria-label': `Role for column ${header}` }}
                      onChange={(event) =>
                        onChange({ ...mapping, [header]: event.target.value as ColumnRole })
                      }
                    >
                      {COLUMN_ROLES.map((option) => (
                        <MenuItem key={option} value={option}>
                          {ROLE_LABELS[option]}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary">
                      {ROLE_HINTS[role]}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {sampleValues(created.sampleRows, header) || '—'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Sample rows as decoded
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Check any accented names here. If they read correctly the decode ladder did its job.
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {created.headers.map((header) => (
                  <TableCell key={header} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {created.sampleRows.map((row, index) => (
                // Sample rows have no identifier of their own and are never
                // reordered, so the index is the only stable key available.
                <TableRow key={index}>
                  {created.headers.map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        whiteSpace: 'nowrap',
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row[header] ?? ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  )
}
